// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility, {ChannelTypingIndicatorMode} from '@app/features/accessibility/state/Accessibility';
import {SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {ChannelItemCore} from '@app/features/app/components/layout/ChannelItem';
import channelItemSurfaceStyles from '@app/features/app/components/layout/ChannelItemSurface.module.css';
import {Typing} from '@app/features/channel/components/Typing';
import {cdnUrl} from '@app/features/messaging/utils/UrlUtils';
import {AvatarStack} from '@app/features/ui/avatars/AvatarStack';
import {Switch} from '@app/features/ui/components/form/Switch';
import {MockAvatar} from '@app/features/ui/components/MockAvatar';
import type {RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import styles from '@app/features/user/components/modals/tabs/appearance_tab/InterfaceTab.module.css';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const ChannelListPreview = observer(({mode}: {mode: ChannelTypingIndicatorMode}) => {
	const typingIndicator =
		mode !== ChannelTypingIndicatorMode.HIDDEN ? (
			<Tooltip
				text={() => (
					<span className={styles.tooltipContent}>
						<strong>Kenji</strong>, <strong>Amara</strong> and <strong>Mateo</strong> are typing...
					</span>
				)}
			>
				<div className={styles.typingContainer}>
					<Typing className={styles.typingAnimationWrapper} color="var(--surface-interactive-selected-color)" />
					{mode === ChannelTypingIndicatorMode.AVATARS && (
						<AvatarStack size={12} maxVisible={5} className={styles.typingAvatars}>
							{[1, 2, 3].map((index) => (
								<MockAvatar
									key={index}
									size={12}
									userTag={`User ${index}`}
									avatarUrl={cdnUrl(`avatars/${index}.png`)}
								/>
							))}
						</AvatarStack>
					)}
				</div>
			</Tooltip>
		) : undefined;
	return (
		<div className={styles.previewContainer}>
			<div className={styles.previewContent}>
				<ChannelItemCore
					channel={{name: 'general', type: 0}}
					isSelected={true}
					typingIndicator={typingIndicator}
					className={clsx(
						'cursor-default',
						channelItemSurfaceStyles.channelItemSurface,
						channelItemSurfaceStyles.channelItemSurfaceSelected,
					)}
				/>
			</div>
		</div>
	);
});
export const InterfaceTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	const channelTypingIndicatorOptions: ReadonlyArray<RadioOption<ChannelTypingIndicatorMode>> = [
		{
			value: ChannelTypingIndicatorMode.AVATARS,
			name: t`Typing Indicator + Avatars`,
			desc: t`Show typing indicator with user avatars in the channel list`,
		},
		{
			value: ChannelTypingIndicatorMode.INDICATOR_ONLY,
			name: t`Typing Indicator Only`,
			desc: t`Show just the typing indicator without avatars`,
		},
		{
			value: ChannelTypingIndicatorMode.HIDDEN,
			name: t`Hidden`,
			desc: t`Don't show typing indicators in the channel list`,
		},
	];
	return (
		<>
			<SettingsTabSection
				title={t`Channel list typing indicators`}
				description={t`Choose how typing indicators appear in the channel list when someone is typing in a channel.`}
			>
				<ChannelListPreview mode={Accessibility.channelTypingIndicatorMode} />
				<RadioGroup
					options={channelTypingIndicatorOptions}
					value={Accessibility.channelTypingIndicatorMode}
					onChange={(value) => AccessibilityCommands.update({channelTypingIndicatorMode: value})}
					aria-label={t`Channel list typing indicator mode`}
				/>
				<div className={styles.switchWrapper}>
					<Switch
						label={t`Show typing on selected channel`}
						description={t`When disabled (default), typing indicators won't appear on the channel you're currently viewing.`}
						value={Accessibility.showSelectedChannelTypingIndicator}
						onChange={(value) => AccessibilityCommands.update({showSelectedChannelTypingIndicator: value})}
					/>
				</div>
			</SettingsTabSection>
			<SettingsTabSection
				title={t`Keyboard Hints`}
				description={t`Control whether keyboard shortcut hints appear inside tooltips.`}
			>
				<div className={styles.switchWrapper}>
					<Switch
						label={t`Hide keyboard hints in tooltips`}
						description={t`When enabled, shortcut badges are hidden in tooltip popups.`}
						value={Accessibility.hideKeyboardHints}
						onChange={(value) => AccessibilityCommands.update({hideKeyboardHints: value})}
					/>
				</div>
			</SettingsTabSection>
			<SettingsTabSection
				title={t`Voice channel join behavior`}
				description={t`Control how you join voice channels in communities.`}
			>
				<Switch
					label={t`Confirm before joining voice channels`}
					description={t`Ask for confirmation before joining voice channels in communities.`}
					value={Accessibility.confirmBeforeJoiningVoiceChannels}
					onChange={(value) =>
						AccessibilityCommands.update({
							confirmBeforeJoiningVoiceChannels: value,
						})
					}
				/>
			</SettingsTabSection>
		</>
	);
});
